/**
 * The frontend's view of module authorization.
 *
 * Reads the effective access the API returned on /me and answers the same
 * questions the backend guard does — using the SAME algebra, imported from
 * @yazamco/validation, so the two cannot disagree about what a level implies.
 *
 * This is presentation only. Hiding a module keeps the UI honest and stops
 * accidental clicks; it is not a security boundary, and every one of these
 * questions is asked again server-side on the request itself. A user who edits
 * their own JavaScript gains nothing but a screen that 403s.
 */
import type {
  AccessLevel,
  ActionKey,
  EffectiveAccess,
  ModuleKey,
} from "@yazamco/shared-types";
import { can, canViewModule, emptyAccess, levelFor, levelSatisfies } from "@yazamco/validation";
import { useAuth } from "./AuthContext";

export interface AccessApi {
  access: EffectiveAccess;
  /** Whether the principal may perform this action. */
  can: (action: ActionKey) => boolean;
  /** The principal's level for a module (`none` when absent). */
  levelOf: (module: ModuleKey) => AccessLevel;
  /** Whether the module is visible at all (View or better). */
  canView: (module: ModuleKey) => boolean;
  /** Whether the principal holds at least `level` on the module. */
  atLeast: (module: ModuleKey, level: AccessLevel) => boolean;
  isSuperAdmin: boolean;
}

export function useAccess(): AccessApi {
  const { user } = useAuth();
  // Fail CLOSED while /me is loading or if the field is absent: showing nothing
  // briefly is correct, whereas showing everything briefly is a flash of UI the
  // user cannot use.
  const access = user?.access ?? emptyAccess();

  return {
    access,
    can: (action) => can(access, action),
    levelOf: (module) => levelFor(access, module),
    canView: (module) => canViewModule(access, module),
    atLeast: (module, level) => levelSatisfies(levelFor(access, module), level),
    isSuperAdmin: access.isSuperAdmin,
  };
}
